import {
  CardContent,
  Chip,
  Grid,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import { MUIDataTableColumn } from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Card from '../../../../components/Card';
import CardMessage from '../../../../components/CardMessage/CardMessage';
import CustomDataTable from '../../../../components/CustomDataTable';
import SelectPeriod from '../../../../components/SelectPeriod';
import TableSkeleton from '../../../../components/TableSkeleton';
import { EmployeeDTO } from '../../../../models/dto/EmployeeDTO';
import { NoticeDTO } from '../../../../models/dto/NotificationDTO';
import { PeriodDTO } from '../../../../models/dto/PeriodDTO';
import { findNoticesyEmployeeAndPeriod } from '../../../../services/personnelDepartment.service';
import { AppState } from '../../../../store';
import { EmployeesReducerData } from '../../../../store/reducers/employeesReducer';

type TableDataFilterOptions = 'ALL' | 'NOT_VIEWED' | 'VISUALIZED';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chip: {
      margin: '5px 0 5px 10px',
    },
    root: {
      height: '100%',
    },
    cardHeaderContainer: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: 0,
    },
    cardContentContainer: {
      height: 'calc(100% - 54px)',
    },
  }),
);

const EmployeeSentNoticesTableCard: React.FC = () => {
  const classes = useStyles();
  const [tableData, setTableData] = useState<NoticeDTO[]>([]);
  const [periodSelected, setPeriodSelected] = useState<PeriodDTO | null>(null);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [chipFilterSelected, setChipFilterSelected] = useState<
    TableDataFilterOptions
  >('ALL');

  const { employeeSelected }: EmployeesReducerData = useSelector(
    (state: AppState) => state.employeesReducer,
  );

  const columns: MUIDataTableColumn[] = [
    {
      name: 'createdAt',
      label: 'Data de Envio',
    },
    {
      name: 'visualizedAt',
      label: 'Data de Visualização',
    },
    {
      name: 'title',
      label: 'Título',
    },
    {
      name: 'message',
      label: 'Texto do Aviso',
    },
  ];

  const handleChipFilterSelected = (chipSelected: TableDataFilterOptions) => {
    setChipFilterSelected(chipSelected);
  };

  const getTableData = async (employee: EmployeeDTO, period: PeriodDTO) => {
    try {
      setLoadingData(true);
      const { data } = await findNoticesyEmployeeAndPeriod(employee, period);

      setTableData(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (employeeSelected && periodSelected) {
      getTableData(employeeSelected, periodSelected);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeSelected, periodSelected]);

  return (
    <Card style={{ height: '100%' }}>
      <Grid
        container
        alignItems="center"
        justify="space-between"
        className={classes.cardHeaderContainer}
      >
        <Grid item>
          <Typography color="textSecondary" variant="h5">
            Avisos enviados
          </Typography>
        </Grid>
        <Grid item>
          <SelectPeriod
            showLabel
            disableUnderline
            value={periodSelected}
            handlePeriodChange={setPeriodSelected}
          />
        </Grid>
      </Grid>

      <CardContent className={classes.cardContentContainer}>
        {loadingData ? (
          <TableSkeleton rows={3} />
        ) : (
          <>
            <Grid container spacing={0}>
              <Grid item>
                <Chip
                  variant={
                    chipFilterSelected === 'ALL' ? 'default' : 'outlined'
                  }
                  className={classes.chip}
                  clickable
                  onClick={() => handleChipFilterSelected('ALL')}
                  size="medium"
                  label="Todos"
                />
                <Tooltip
                  arrow
                  title="Faltas Sem Justificativas - Atrasos Sem Justificativas - Saídas Antecipadas Sem Justificativas - Horas de Atestado"
                >
                  <Chip
                    variant={
                      chipFilterSelected === 'VISUALIZED'
                        ? 'default'
                        : 'outlined'
                    }
                    className={classes.chip}
                    clickable
                    onClick={() => handleChipFilterSelected('VISUALIZED')}
                    size="medium"
                    label="Visualizados"
                  />
                </Tooltip>
                <Chip
                  variant={
                    chipFilterSelected === 'NOT_VIEWED' ? 'default' : 'outlined'
                  }
                  className={classes.chip}
                  clickable
                  onClick={() => handleChipFilterSelected('NOT_VIEWED')}
                  size="medium"
                  label="Não Visualizados"
                />
              </Grid>
            </Grid>

            {employeeSelected ? (
              <>
                {loadingData ? (
                  <TableSkeleton rows={3} />
                ) : (
                  <CustomDataTable
                    data={tableData
                      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                      .filter(item => {
                        switch (chipFilterSelected) {
                          case 'ALL':
                            return true;

                          case 'NOT_VIEWED':
                            return !item.visualizedAt;

                          default:
                            return item.visualizedAt;
                        }
                      })}
                    columns={columns}
                    options={{
                      elevation: 0,
                      tableBodyHeight: 'calc(100% - 150px)',
                    }}
                    title=""
                  />
                )}
              </>
            ) : (
              <CardMessage
                icon={<PersonIcon style={{ fontSize: 130, opacity: 0.4 }} />}
                message="Selecione um funcionário"
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeSentNoticesTableCard;
